/**
 * INVOICE MANAGER - List, generate, and manage invoices
 */

import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw, AlertCircle, FileText, Check, X, Eye } from 'lucide-react';
import InvoiceGenerator from '../components/admin/financials/InvoiceGenerator';
import InvoicePDF from '../components/admin/financials/InvoicePDF';
import FinancialNav from '../components/admin/financials/FinancialNav';
import TenantSelector from '../components/admin/financials/TenantSelector';
import apiClient from '../lib/apiClient';

export default function InvoiceManager() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [filters, setFilters] = useState({ status: '' });
  const [tenantFilter, setTenantFilter] = useState('all');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [lineItems, setLineItems] = useState([]);

  useEffect(() => {
    loadInvoices();
  }, [pagination.page, filters, tenantFilter]);

  const loadInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      });

      if (tenantFilter !== 'all') {
        params.append('tenant_id', tenantFilter);
      }

      const response = await apiClient.get(`/admin/financials/invoices?${params}`);

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      setInvoices(response.data?.data || []);
      setPagination(response.data?.pagination || { page: 1, limit: 50, total: 0 });
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError(err.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (invoiceId) => {
    try {
      const response = await apiClient.patch(`/admin/financials/invoices/${invoiceId}`, {
        status: 'paid',
        paid_date: new Date().toISOString().split('T')[0]
      });

      if (response.data) {
        alert('Invoice marked as paid! ✅');
        loadInvoices();
      }
    } catch (err) {
      console.error('Error marking invoice as paid:', err);
      alert('Error updating invoice: ' + (err.message || 'Unknown error'));
    }
  };

  const handleViewPDF = async (invoice) => {
    setSelectedInvoice(invoice);

    // Load line items
    try {
      const response = await apiClient.get(`/admin/financials/invoices/${invoice.id}/line-items`);
      if (response.data) {
        setLineItems(response.data || []);
      }
    } catch (err) {
      console.error('Error loading line items:', err);
    }

    setShowPDFModal(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid: { bg: 'bg-green-100', text: 'text-green-800', icon: <Check className="w-3 h-3" /> },
      sent: { bg: 'bg-blue-100', text: 'text-blue-800', icon: <FileText className="w-3 h-3" /> },
      overdue: { bg: 'bg-red-100', text: 'text-red-800', icon: <AlertCircle className="w-3 h-3" /> },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', icon: <X className="w-3 h-3" /> },
      draft: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: <FileText className="w-3 h-3" /> }
    };

    const badge = badges[status] || badges.draft;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.icon}
        {status}
      </span>
    );
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              📄 Invoice Management
            </h1>
            <p className="text-gray-600 mt-2">Generate and manage customer invoices</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowGenerateModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 shadow-sm transition-colors"
            >
              <Plus className="w-5 h-5" />
              Generate New Invoice
            </button>
            <button
              onClick={loadInvoices}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 items-center p-4 bg-white rounded-lg shadow-sm">
          <div className="flex gap-2">
            <label className="text-sm font-medium text-gray-700">Status:</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="text-sm border border-gray-300 rounded px-3 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="w-px h-6 bg-gray-300"></div>
          <TenantSelector value={tenantFilter} onChange={setTenantFilter} />
        </div>
      </div>

      {/* Navigation */}
      <FinancialNav />

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-900">Failed to load invoices</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
            <button
              onClick={loadInvoices}
              className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Try again →
            </button>
          </div>
        </div>
      )}

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            All Invoices ({pagination.total})
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-3 font-medium">Loading invoices...</span>
                    </div>
                  </td>
                </tr>
              ) : invoices.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-lg font-medium text-gray-700">No invoices found</p>
                    <p className="text-sm text-gray-500 mt-1">Generate your first invoice to get started</p>
                    <button
                      onClick={() => setShowGenerateModal(true)}
                      className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                      Generate Invoice
                    </button>
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-blue-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {invoice.invoice_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {invoice.customer_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(invoice.invoice_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(invoice.period_start).toLocaleDateString()} - {new Date(invoice.period_end).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-bold">
                      ${parseFloat(invoice.total).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getStatusBadge(invoice.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => handleViewPDF(invoice)}
                          className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                        {invoice.status !== 'paid' && (
                          <button
                            onClick={() => handleMarkAsPaid(invoice.id)}
                            className="text-green-600 hover:text-green-900 flex items-center gap-1"
                          >
                            <Check className="w-4 h-4" />
                            Mark Paid
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === Math.ceil(pagination.total / pagination.limit)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Generator Modal */}
      <InvoiceGenerator
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onGenerated={() => {
          setShowGenerateModal(false);
          loadInvoices();
        }}
      />

      {/* PDF Viewer Modal */}
      {showPDFModal && selectedInvoice && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Invoice Preview</h3>
              <button
                onClick={() => setShowPDFModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <InvoicePDF invoice={selectedInvoice} lineItems={lineItems} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
